import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Switch,
	TextField,
} from '@mui/material';
import { ClassicEditor } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '../../components/Button/Button.tsx';
import { Footer } from '../../components/Footer/Footer.tsx';
import { Header } from '../../components/Header/Header.tsx';
import Hero from '../../components/Hero/Hero.tsx';
import { LessonCreateFailureReasonView, LessonDifficultyLevelView } from '../../graphql/types.gen.ts';
import { GENERAL_GROUP_NAME } from '../../index.tsx';
import { EMPTY_TEST } from '../Backoffice/Backoffice.tsx';
import { GroupTagsDocument, useExamRemoveMutation, useGroupTagsQuery } from '../Backoffice/backoffice.gen.ts';
import { BackofficeTest } from '../Backoffice/components/BackofficeTest.tsx';
import { useLastGroupQuery } from '../FormPage/LoginPage/loginPage.gen.ts';
import './Lessons.scss';
import './SingleLesson/SingleLesson.scss';
import { editorConfig, slugify } from './helpers/Lessons.helpers.ts';
import {
	GetLessonsHomeworkBySlugDocument,
	useCreateLessonMutation,
	useEditLessonMutation,
	useGetLessonBySlugLazyQuery,
	useGetLessonsHomeworkBySlugLazyQuery,
} from './lessons.gen.ts';

export const AddLesson = () => {
	const { titleId } = useParams();
	const { data: chosenGroupData } = useLastGroupQuery();
	const chosenGroup = chosenGroupData?.me?.recentlyUsedGroupId || GENERAL_GROUP_NAME;
	const navigate = useNavigate();
	const backofficeTests = useRef(null);
	const editorRef = useRef(null); // ? mb rm
	const editorContainerRef = useRef(null); // ? mb rm

	const [examRemoveMutation] = useExamRemoveMutation({
		refetchQueries: [GroupTagsDocument, GetLessonsHomeworkBySlugDocument],
	});
	const [editLessonMutation] = useEditLessonMutation({ refetchQueries: [GetLessonsHomeworkBySlugDocument] });
	const [createLessonMutation] = useCreateLessonMutation();

	const [editedTestId, setEditedTestId] = useState(null);
	const [tag, setTag] = useState('');
	const [name, setName] = useState('');
	const [newtag, setNewTag] = useState(false);
	const [isLayoutReady, setIsLayoutReady] = useState(false);
	const [emptyTest, setEmptyTest] = useState(false);
	const [stage2Id, setStage2Id] = useState(titleId || '');
	const [editorContent, setEditorContent] = useState('');
	const [difficultyLevel, setDifficultyLevel] = useState<LessonDifficultyLevelView>(
		LessonDifficultyLevelView.Beginner
	);

	const { data, loading } = useGroupTagsQuery({
		variables: {
			code: chosenGroup,
		},
	});

	const [fetchLessonQuery, { data: lesson, refetch: refreshLesson }] = useGetLessonBySlugLazyQuery();

	const fetchLesson = () => {
		fetchLessonQuery({
			variables: {
				code: chosenGroup,
				lessonInput2: {
					slug: stage2Id,
				},
			},
		});
	};

	const [fetchHomeworkQuery, { data: lessonsHomework, refetch: refreshHomework }] =
		useGetLessonsHomeworkBySlugLazyQuery();

	const fetchHomework = () => {
		fetchHomeworkQuery({
			variables: {
				code: chosenGroup,
				lessonInput2: {
					slug: stage2Id,
				},
			},
		});
	};

	const onSave = (id) => {
		toast.success('Test zaktualizowany pomyślnie');
		setEmptyTest(false);
		handleEdit(id);
	};

	const onTestEdit = () => {
		toast.success('Test zaktualizowany pomyślnie');
		setEmptyTest(false);
		refreshLesson();
		refreshHomework();
	};

	useEffect(() => {
		if (!lesson?.group.lesson) return;
		setTag(lesson?.group.lesson.tags[0]);
		setName(lesson?.group.lesson.title);
		setEditorContent(lesson?.group.lesson.content);
		setDifficultyLevel(lesson?.group.lesson.difficultyLevel);
	}, [lesson?.group?.lesson]);

	useEffect(() => {
		if (!stage2Id) return;
		fetchLesson();
		fetchHomework();
	}, [chosenGroup, stage2Id]);

	useEffect(() => {
		setIsLayoutReady(true);
		return () => setIsLayoutReady(false);
	}, []);

	const handleRemoveClick = async (id: string) => {
		const response = await examRemoveMutation({
			variables: {
				input: {
					examId: id,
					groupCode: chosenGroup,
				},
			},
		});
		if (response.data?.examDelete?.reason === null) {
			toast.success('Test został usunięty');
			refreshHomework();
		} else {
			toast.error(`Nie udało się usunąć testu: ${response.data?.examDelete?.reason}`);
		}
	};

	const addEmptyTest = () => {
		setEmptyTest(true);
		scrollToBottom();
	};

	const scrollToBottom = () => {
		backofficeTests.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const handleEditorChange = (event, editor) => {
		const data = editor && editor.getData();
		setEditorContent(data);
	};

	const handleTagChange = (event: SelectChangeEvent<string>) => {
		const {
			target: { value },
		} = event;
		setTag(value);
	};

	const handleDifficultyLevelChange = (event: SelectChangeEvent<LessonDifficultyLevelView>) => {
		const {
			target: { value },
		} = event;
		setDifficultyLevel(value as LessonDifficultyLevelView);
	};

	const handleEdit = async (testId?: string) => {
		const currentTests = lessonsHomework?.group
			? lessonsHomework?.group?.lesson?.homework?.map((homework) => homework.id)
			: [];
		const response = await editLessonMutation({
			variables: {
				input: {
					groupCode: chosenGroup,
					revision: {
						title: name,
						tags: [tag],
						content: editorContent,
						description: '',
						homeworkExamIds: testId ? [...currentTests, testId] : currentTests,
						difficultyLevel,
						slug: stage2Id,
					},
				},
			},
		});
		if (response.data?.lessonRevisionCreate?.__typename === 'LessonRevisionCreateSuccessView') {
			setStage2Id(response.data?.lessonRevisionCreate.lessonSlug);
			refreshLesson();
			refreshHomework();
			toast.success('Lekcja zaktualizowana pomyślnie');
		} else {
			handleError(response.data?.lessonRevisionCreate?.reason);
		}
	};

	const handleSubmit = async () => {
		const response = await createLessonMutation({
			variables: {
				input: {
					groupCode: chosenGroup,
					insertBeforeLessonSlug: null,
					revision: {
						title: name,
						tags: [tag],
						content: editorContent,
						description: '',
						homeworkExamIds: [],
						difficultyLevel,
						slug: `${slugify(name)}`,
					},
				},
			},
		});
		if (response.data?.lessonCreate?.__typename === 'LessonCreateSuccessView') {
			setStage2Id(response.data?.lessonCreate.lessonSlug);
			toast.success('Lekcja zaktualizowana pomyślnie, możesz teraz dodać testy');
			navigate(`/lekcje/edytuj/${response.data?.lessonCreate.lessonSlug}`);
		} else {
			handleError(response.data?.lessonCreate?.reason);
		}
	};

	const handleError = (error) => {
		switch (error) {
			case LessonCreateFailureReasonView.SlugAlreadyExists:
			case LessonCreateFailureReasonView.TitleAlreadyExists:
				toast.error('Lekcja o takim tytule już istnieje');
				break;
			case LessonCreateFailureReasonView.GroupNotFound:
				toast.error('Grupa do której chcesz dodac lekcję nie istnieje');
				break;
			case LessonCreateFailureReasonView.CreatorNotGroupInstructor:
				toast.error('Nie jesteś instruktorem tej grupy');
				break;
			case LessonCreateFailureReasonView.InvalidSlug:
				toast.error('Nieprawidłowy slug - tytuł lekcji');
				break;
			case LessonCreateFailureReasonView.LessonToInsertBeforeNotFound:
				toast.error('Nie znaleziono lekcji do której chcesz dodać nową');
				break;
			default:
				toast.error('Coś poszło nie tak');
				break;
		}
	};

	const handleTextTagChange = (event) => {
		const {
			target: { value },
		} = event;
		setTag(value);
	};

	const handleNewTagChange = () => {
		setNewTag((prev) => !prev);
		setTag('');
	};

	const isValid = name?.length > 0 && tag?.length > 0 && editorContent?.length > 0;

	// useValidateToken();

	return (
		<div className="lessons lesson__editLesson light">
			<div className="content">
				<Header light={true} />
				<Hero title="Dodaj lekcję" />
				<div className="lessons__container container">
					<Paper className="lessons__paper lessons__paper--evenly">
						<h2 className="lessons__paperTitle">Konfiguracja lekcji</h2>
						<div className="row">
							{!!data?.group?.possibleExamTags.length && (
								<FormControlLabel
									control={<Switch />}
									label="Dodaj nowy tag"
									onChange={handleNewTagChange}
								/>
							)}
							{newtag || !data?.group?.possibleExamTags.length ? (
								<TextField
									inputProps={{ maxLength: 30 }}
									id="outlined-basic"
									label="Tag*"
									variant="outlined"
									onChange={handleTextTagChange}
								/>
							) : (
								<FormControl style={{ minWidth: 220 }}>
									<InputLabel id="tag-label">Tag*</InputLabel>
									<Select
										labelId="tag-label"
										id="tag"
										value={tag}
										label="Tag*"
										onChange={handleTagChange}
									>
										{data?.group?.possibleExamTags.map((tag) => (
											<MenuItem key={tag} value={tag}>
												{tag}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
							<FormControl style={{ minWidth: 120 }}>
								<TextField
									inputProps={{ maxLength: 40 }}
									id="outlined-controlled"
									label="Tytuł lekcji"
									value={name}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										setName(event.target.value);
									}}
									required
								/>
							</FormControl>
							<FormControl style={{ minWidth: 180 }}>
								<InputLabel id="lesson-difficulty-level-label">Poziom trudności</InputLabel>
								<Select
									labelId="lesson-difficulty-level-label"
									id="lesson-difficulty-level"
									value={difficultyLevel}
									label="Poziom trudności"
									onChange={handleDifficultyLevelChange}
								>
									<MenuItem value={LessonDifficultyLevelView.Beginner}>Początkujący</MenuItem>
									<MenuItem value={LessonDifficultyLevelView.Intermediate}>
										Średniozaawansowany
									</MenuItem>
									<MenuItem value={LessonDifficultyLevelView.Advanced}>Zaawansowany</MenuItem>
								</Select>
							</FormControl>
						</div>
						<h2 className="lessons__paperTitle">Treść lekcji</h2>
						<div className="main-container">
							<div className="editor-container editor-container_classic-editor" ref={editorContainerRef}>
								<div className="editor-container__editor">
									<div ref={editorRef}>
										{isLayoutReady && (
											<CKEditor
												style={{ width: '100%' }}
												editor={ClassicEditor}
												data={editorContent}
												config={editorConfig}
												onChange={handleEditorChange}
											/>
										)}
									</div>
								</div>
							</div>
						</div>
						{!stage2Id ? (
							<Button disabled={!isValid} text="Przejdź dalej" onClick={handleSubmit} />
						) : (
							<div className="row justify--center">
								<Button
									variant="ghost-gold"
									text="Wróć"
									onClick={() => {
										navigate(`/nauka`);
									}}
								/>
								<Button
									variant="ghost-gold"
									text="Zapisz zmiany"
									onClick={() => {
										handleEdit();
									}}
								/>
								<Button
									variant="ghost-gold"
									text="Zobacz lekcję"
									onClick={() => {
										navigate(`/${chosenGroup}/lekcja/${stage2Id}`);
									}}
								/>
							</div>
						)}
					</Paper>
				</div>
				<div className={`lesson__homework ${stage2Id ? '' : 'lesson__homework--disabled'}`}>
					<div className="lessons__container container">
						<h2 className="lessons__paperTitle">Testy do zrobienia w lekcji</h2>
					</div>
					{!emptyTest && !loading && (
						<Button
							customClass="backoffice__add"
							variant="ghost-gold"
							text={'Dodaj'}
							onClick={addEmptyTest}
						/>
					)}

					{lessonsHomework &&
						lessonsHomework?.group?.lesson?.homework.map((homework, index) => {
							return (
								<BackofficeTest
									key={homework.id}
									isForLesson={true}
									lessonSlug={stage2Id}
									index={index + 1}
									test={homework}
									onSave={onTestEdit}
									editMode={editedTestId === homework.id}
									handleLeaveEditClick={() => {
										setEditedTestId(null);
									}}
									handleEditClick={() => {
										setEditedTestId(homework.id);
									}}
									handleRemoveClick={handleRemoveClick}
								/>
							);
						})}
					<div ref={backofficeTests}></div>
					{emptyTest && (
						<BackofficeTest
							index={lessonsHomework?.group?.lesson?.homework?.length + 1}
							key="empty"
							isForLesson={true}
							test={{
								...EMPTY_TEST,
								tags: [tag],
								name: new Date().toISOString().slice(2, 17).replace(/[-T:]/g, ''),
							}}
							onSave={onSave}
							editMode={true}
							handleLeaveEditClick={() => {
								setEmptyTest(false);
								setEditedTestId(null);
							}}
							handleEditClick={() => {}}
							handleRemoveClick={handleRemoveClick}
						/>
					)}
				</div>
			</div>
			<Footer />
		</div>
	);
};
